/**
 * Google Analytics 4 — Consent Mode v2 loader
 * ------------------------------------------------------------------
 * Loads NO network resources and sets NO cookies until the visitor
 * explicitly grants analytics consent via the cookie banner.
 *
 * Compliance behaviour:
 *   • On mount: creates a dormant gtag stub + pushes consent='default'
 *     with every category set to 'denied' except functionality_storage
 *     and security_storage (both covered by GDPR Art. 5(3) essentials).
 *   • Does NOT inject the googletagmanager.com <script> tag until
 *     grantAnalyticsConsent() is called.
 *   • On "deny": removes any pre-existing _ga*, _gid, _gat, _gac* cookies.
 *   • On repeat visits that previously accepted (localStorage.cookie_consent
 *     has analytics:true): auto-grants and loads GA.
 *
 * The GA Measurement ID is read from REACT_APP_GA_MEASUREMENT_ID in
 * frontend/.env. If unset, the loader is fully inert (safe default).
 */

import { useEffect } from 'react';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';
const SCRIPT_ID = 'ga4-gtag-loader';

function readConsentFromStorage() {
  try {
    const raw = localStorage.getItem('cookie_consent');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function deleteGaCookies() {
  const names = ['_ga', '_gid', '_gat', '_gac'];
  const host = window.location.hostname;
  // Strip subdomain to also clear parent-domain cookies (e.g. .sptdigitalmarketing.com)
  const parts = host.split('.');
  const parentDomain = parts.length > 1 ? '.' + parts.slice(-2).join('.') : host;
  document.cookie.split(';').forEach((c) => {
    const cookie = c.trim();
    const eq = cookie.indexOf('=');
    const name = eq > -1 ? cookie.substring(0, eq) : cookie;
    if (names.some((n) => name === n || name.startsWith(n + '_') || name.startsWith('_ga_'))) {
      // Expire on current path/domain and parent domain
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${host}`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${parentDomain}`;
    }
  });
}

function loadGAScript() {
  if (!GA_MEASUREMENT_ID) return;
  if (document.getElementById(SCRIPT_ID)) return; // already loaded
  const s = document.createElement('script');
  s.id = SCRIPT_ID;
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { anonymize_ip: true });
}

/** Call when the visitor clicks "Accept all".
 *  ONLY grants analytics_storage. All ad_* categories remain 'denied' because
 *  our Privacy Policy explicitly promises no advertising cookies, ad profiles,
 *  or cross-site tracking. Keeping ad_* denied prevents Google from using the
 *  measurement data for ads personalization even if the account is later
 *  linked to Google Ads. */
export function grantAnalyticsConsent() {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('consent', 'update', {
    analytics_storage: 'granted',
    // ad_* intentionally omitted → stay at the 'denied' default set on load
  });
  loadGAScript();
}

/** Call when the visitor clicks "Essential only" or "Withdraw consent". */
export function denyAnalyticsConsent() {
  if (typeof window === 'undefined') return;
  if (window.gtag) {
    window.gtag('consent', 'update', {
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      ad_storage: 'denied',
      analytics_storage: 'denied',
    });
  }
  // Remove the <script> tag if present so no further beacons fire
  const el = document.getElementById(SCRIPT_ID);
  if (el && el.parentNode) el.parentNode.removeChild(el);
  deleteGaCookies();
}

const GoogleAnalytics = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Set up dataLayer + gtag stub (NO network calls yet)
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function gtag() { window.dataLayer.push(arguments); };

    // Deny-by-default consent (GDPR / FTC safe baseline)
    window.gtag('consent', 'default', {
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      ad_storage: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'granted', // essential — session, i18n
      security_storage: 'granted',       // essential — anti-fraud
      wait_for_update: 500,
    });

    // Returning visitor who previously accepted → auto-grant
    // BUT: if GPC signal is active now, respect it and do NOT auto-load even
    // with prior consent. CPRA §7025(c) treats GPC as an active opt-out.
    const gpcActive = (typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true);
    const prev = readConsentFromStorage();
    if (prev && prev.analytics === true && !gpcActive) {
      grantAnalyticsConsent();
    }
  }, []);

  return null;
};

export default GoogleAnalytics;
